import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Container,
  Group,
  Illustration,
  SectionBannerContent,
  SectionBannerDescription,
  SectionBannerIcon,
  SectionBannerRoot,
  Text,
} from '@melio/penny';

import { addMfaFactor } from '../../api/auth';
import { useAnalytics, type Cta, type ErrorType } from '../../analytics';
import { useVariant } from '../../contexts/VariantContext';
import { getEntryPoint } from '../../session';
import { useMfaNavigation } from '../../hooks/useMfaNavigation';
import { completeAuthRedirect } from '../../hooks/useRedirectParam';
import { clearStoredMfaEnrollmentOffer, readStoredMfaEnrollmentOffer } from '../../hooks/useMfaEnrollmentOffer';
import { formatE164ForDisplay } from '../PhoneNumberField/phoneNumber';
import { PhoneNumberField, type PhoneNumberChange, type PhoneNumberValue } from '../PhoneNumberField/PhoneNumberField';
import { FactorSelector, type FactorOption } from './FactorSelector';
import { FACTOR_COPY, type AvailableFactor } from './factorCopy';

const INVALID_PHONE_NUMBER_MESSAGE = 'Please enter a valid phone number.';

export const MfaEnrollOfferPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { track } = useAnalytics();
  const { variantConfig } = useVariant();
  const { navigateToMfaChallenge } = useMfaNavigation();

  const [offer, setOffer] = useState<ReturnType<typeof readStoredMfaEnrollmentOffer>>(null);
  const [selectedFactor, setSelectedFactor] = useState<AvailableFactor | null>(null);
  const [phone, setPhone] = useState<PhoneNumberValue>({ country: 'US', nationalNumber: '' });
  const [phoneE164, setPhoneE164] = useState<string | undefined>(undefined);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isPhoneInputInvalid, setIsPhoneInputInvalid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const entryPoint = getEntryPoint();
  const flow = entryPoint === 'sign-up' ? 'sign-up' : 'sign-in';
  const activeFactor: AvailableFactor = selectedFactor ?? offer?.available_factors[0] ?? 'phone';
  const copy = FACTOR_COPY[activeFactor];

  document.title = copy.offerTitle;

  // The skip-experiment bucket (control vs. skip-hidden) so drop-off on this screen can be split
  // by arm - the whole point of running the experiment, not just an implementation detail.
  const bucketProperties = (hideSkipValue: boolean) => ({ Variant: hideSkipValue ? 'skip-hidden' : 'control' });

  const trackView = (hideSkipValue: boolean) => {
    track({
      eventName: 'Connection-View',
      properties: {
        PageName: 'add-your-phone-number',
        Flow: flow,
        Intent: flow,
        EntryPoint: entryPoint,
        ...bucketProperties(hideSkipValue),
      },
    });
  };

  const trackClick = (cta: Cta) => {
    track({
      eventName: 'Connection-Click',
      properties: {
        PageName: 'add-your-phone-number',
        Flow: flow,
        Intent: flow,
        EntryPoint: entryPoint,
        Cta: cta,
        PhoneInteraction: isPhoneValid,
        ...bucketProperties(hideSkip),
      },
    });
  };

  const trackStatus = (statusType: 'success' | 'failure', errorType?: ErrorType) => {
    track({
      eventName: 'Connection-Status',
      properties: {
        PageName: 'add-your-phone-number',
        Flow: flow,
        Intent: flow,
        EntryPoint: entryPoint,
        Cta: 'send-code',
        StatusType: statusType,
        ...(errorType && { ErrorType: errorType }),
        ...bucketProperties(hideSkip),
      },
    });
  };

  useEffect(() => {
    const stored = readStoredMfaEnrollmentOffer();

    if (!stored) {
      navigate({ pathname: '/sign-in', search: location.search }, { replace: true });
      return;
    }

    setOffer(stored);
    setSelectedFactor(stored.available_factors[0]);
    trackView(variantConfig?.hideMfaSkip ?? stored.hide_skip ?? false);
  }, [navigate]);

  if (!offer) {
    return null;
  }

  const hideSkip = variantConfig?.hideMfaSkip ?? offer.hide_skip ?? false;
  const factorOptions: FactorOption[] = offer.available_factors.map((factor) => ({
    value: factor,
    label: FACTOR_COPY[factor].selectorLabel,
    icon: FACTOR_COPY[factor].icon,
  }));

  const handlePhoneChange = (value: PhoneNumberChange) => {
    setPhone(value);
    setPhoneE164(value.e164);
    setIsPhoneValid(value.isValid);
    setIsPhoneInputInvalid(false);
  };

  const handleSendCode = async () => {
    trackClick('send-code');

    if (!phoneE164) {
      // Button isn't gated on validity - surface the error here instead.
      setError(INVALID_PHONE_NUMBER_MESSAGE);
      setIsPhoneInputInvalid(true);
      return;
    }

    setIsLoading(true);
    setError(null);
    setIsPhoneInputInvalid(false);

    const result = await addMfaFactor(phoneE164);

    switch (result.type) {
      case 'success':
        trackStatus('success');
        // Not cleared here - the offer must still be readable if the user goes back from the
        // verify screen to change their number. MFAChallengePage clears it on verify success.
        navigateToMfaChallenge(
          {
            mfaToken: result.mfaToken,
            authenticators: [result.authenticator],
            challengedAuthenticators: [result.authenticator.id],
            factor: activeFactor,
            phoneNumber: formatE164ForDisplay(phoneE164),
          },
          '/mfa/enroll/verify'
        );
        setIsLoading(false);
        return;

      case 'invalidPhone':
        trackStatus('failure', 'invalid-phone-number');
        setError(result.message);
        setIsPhoneInputInvalid(true);
        setIsLoading(false);
        return;

      case 'notEligible':
      case 'alreadyEnrolled':
      case 'rateLimited':
      case 'sessionExpired':
      case 'error':
        trackStatus('failure');
        setError(result.message);
        setIsLoading(false);
        return;

      default: {
        const _exhaustiveCheck: never = result;
        return _exhaustiveCheck;
      }
    }
  };

  const handleSkip = () => {
    trackClick('skip');
    clearStoredMfaEnrollmentOffer();
    completeAuthRedirect();
  };

  return (
    <Container height="full" paddingX="s" paddingTop="xxl" data-testid="mfa-enroll-offer-page">
      <Container overflow="visible" style={{ width: '100%', maxWidth: '480px' }}>
        <Group variant="vertical" spacing="l" alignItems="stretch">
          {error && (
            <SectionBannerRoot variant="critical" data-testid="mfa-enroll-offer-error-banner">
              <SectionBannerIcon />
              <SectionBannerContent>
                <SectionBannerDescription>{error}</SectionBannerDescription>
              </SectionBannerContent>
            </SectionBannerRoot>
          )}

          {/* Centered independently of the rest, which stretches full-width (form field, buttons). */}
          <Group variant="vertical" spacing="l" alignItems="center">
            <Illustration size="large" type="security" />

            <Group variant="vertical" spacing="xs" alignItems="center">
              <Text textStyle="heading1Semi" color="semantic.text.primary" data-testid="mfa-enroll-offer-page-title">
                {copy.offerTitle}
              </Text>
              <Text textStyle="body2" color="semantic.text.secondary" style={{ textAlign: 'center' }}>
                {copy.offerSubtitle}
              </Text>
            </Group>
          </Group>

          <FactorSelector
            options={factorOptions}
            selected={selectedFactor ?? ''}
            onSelect={(value) => setSelectedFactor(value as AvailableFactor)}
          />

          {selectedFactor === 'phone' && (
            <PhoneNumberField
              label="Mobile number"
              value={phone}
              onChange={handlePhoneChange}
              testId="mfa-enroll-phone"
              isInvalid={isPhoneInputInvalid}
            />
          )}

          <Group variant="horizontal" spacing="s" alignItems="center" justifyContent="center">
            {!hideSkip && <Button label="Skip" variant="tertiary" size="large" onClick={handleSkip} data-testid="skip-button" />}
            <Button
              label="Send code"
              variant="primary"
              size="large"
              isLoading={isLoading}
              onClick={handleSendCode}
              data-testid="send-code-button"
            />
          </Group>
        </Group>
      </Container>
    </Container>
  );
};
